import { DatabaseAST, DataType } from '../types';

const mapTypeToSQL = (type: DataType, flavor: 'postgres' | 'mysql'): string => {
  const mapping: Record<DataType, string> = {
    uuid: flavor === 'postgres' ? 'UUID' : 'VARCHAR(36)',
    integer: 'INT',
    string: 'VARCHAR(255)',
    text: 'TEXT',
    boolean: 'BOOLEAN',
    timestamp: flavor === 'postgres' ? 'TIMESTAMP' : 'DATETIME',
    datetime: flavor === 'postgres' ? 'TIMESTAMP' : 'DATETIME',
    decimal: 'DECIMAL(10,2)',
    json: flavor === 'postgres' ? 'JSONB' : 'JSON'
  };
  return mapping[type] || 'VARCHAR(255)';
};

export const compileToSQL = (ast: DatabaseAST, flavor: 'postgres' | 'mysql' = 'postgres'): string => {
  let output = `-- Generated by Visual Systems Studio\n-- Flavor: ${flavor}\n\n`;

  ast.tables.forEach(table => {
    output += `CREATE TABLE ${table.name} (\n`;
    const colLines = table.columns.map(col => {
      let line = `  ${col.name} ${mapTypeToSQL(col.type, flavor)}`;
      if (col.isPrimary) line += ' PRIMARY KEY';
      if (!col.isNullable) line += ' NOT NULL';
      if (col.isUnique) line += ' UNIQUE';
      if (col.defaultValue) line += ` DEFAULT ${col.defaultValue}`;
      return line;
    });

    // Foreign Keys
    const tableRelations = ast.relations.filter(r => r.fromTable === table.name);
    tableRelations.forEach(rel => {
      colLines.push(`  CONSTRAINT fk_${rel.fromTable}_${rel.fromColumn} FOREIGN KEY (${rel.fromColumn}) REFERENCES ${rel.toTable}(${rel.toColumn})`);
    });

    output += colLines.join(',\n');
    output += '\n);\n\n';
  });

  // Pivot Tables for Many-to-Many
  ast.relations.filter(r => r.type === 'many_to_many').forEach(rel => {
    const pivotName = [rel.fromTable, rel.toTable].sort().join('_');
    output += `CREATE TABLE ${pivotName} (\n`;
    output += `  ${rel.fromTable.slice(0, -1)}_id ${mapTypeToSQL('uuid', flavor)},\n`;
    output += `  ${rel.toTable.slice(0, -1)}_id ${mapTypeToSQL('uuid', flavor)},\n`;
    output += `  PRIMARY KEY (${rel.fromTable.slice(0, -1)}_id, ${rel.toTable.slice(0, -1)}_id),\n`;
    output += `  FOREIGN KEY (${rel.fromTable.slice(0, -1)}_id) REFERENCES ${rel.fromTable}(${rel.fromColumn}),\n`;
    output += `  FOREIGN KEY (${rel.toTable.slice(0, -1)}_id) REFERENCES ${rel.toTable}(${rel.toColumn})\n`;
    output += `);\n\n`;
  });

  return output;
};

export const compileToPrisma = (ast: DatabaseAST): string => {
  let output = `// Generated by Visual Systems Studio\n\ngenerator client {\n  provider = "prisma-client-js"\n}\n\ndatasource db {\n  provider = "postgresql"\n  url      = env("DATABASE_URL")\n}\n\n`;

  ast.tables.forEach(table => {
    output += `model ${table.name.charAt(0).toUpperCase() + table.name.slice(1)} {\n`;
    table.columns.forEach(col => {
      let type = 'String';
      if (col.type === 'integer') type = 'Int';
      if (col.type === 'boolean') type = 'Boolean';
      if (col.type === 'timestamp' || col.type === 'datetime') type = 'DateTime';
      if (col.type === 'decimal') type = 'Decimal';
      if (col.type === 'json') type = 'Json';

      let line = `  ${col.name} ${type}`;
      if (col.isNullable) line += '?';
      if (col.isPrimary) line += ' @id';
      if (col.defaultValue) line += ` @default(${col.defaultValue})`;
      if (col.isUnique) line += ' @unique';
      
      output += `${line}\n`;
    });

    // Relations in Prisma model
    const outRelations = ast.relations.filter(r => r.fromTable === table.name);
    outRelations.forEach(rel => {
      const targetModel = rel.toTable.charAt(0).toUpperCase() + rel.toTable.slice(1);
      output += `  ${rel.toTable} ${targetModel} @relation(fields: [${rel.fromColumn}], references: [${rel.toColumn}])\n`;
    });

    const inRelations = ast.relations.filter(r => r.toTable === table.name);
    inRelations.forEach(rel => {
      const targetModel = rel.fromTable.charAt(0).toUpperCase() + rel.fromTable.slice(1);
      output += `  ${rel.fromTable}s ${targetModel}[]\n`;
    });

    output += '}\n\n';
  });

  return output;
};

export const compileToLaravel = (ast: DatabaseAST): string => {
  let output = `<?php\n\nuse Illuminate\\Database\\Migrations\\Migration;\nuse Illuminate\\Database\\Schema\\Blueprint;\nuse Illuminate\\Support\\Facades\\Schema;\n\n`;

  ast.tables.forEach(table => {
    output += `// Migration for ${table.name}\n`;
    output += `Schema::create('${table.name}', function (Blueprint $table) {\n`;
    
    table.columns.forEach(col => {
      let typeMethod = 'string';
      if (col.type === 'uuid') typeMethod = 'uuid';
      if (col.type === 'integer') typeMethod = 'integer';
      if (col.type === 'text') typeMethod = 'text';
      if (col.type === 'boolean') typeMethod = 'boolean';
      if (col.type === 'timestamp' || col.type === 'datetime') typeMethod = 'timestamp';
      if (col.type === 'decimal') typeMethod = 'decimal';
      if (col.type === 'json') typeMethod = 'json';

      let line = `    $table->${typeMethod}('${col.name}')`;
      if (col.isNullable) line += '->nullable()';
      if (col.isUnique) line += '->unique()';
      if (col.defaultValue) line += `->default(${col.defaultValue})`;
      if (col.isPrimary) line += '->primary()';
      
      output += `${line};\n`;
    });

    // Foreign Keys
    const tableRelations = ast.relations.filter(r => r.fromTable === table.name);
    tableRelations.forEach(rel => {
      output += `    $table->foreign('${rel.fromColumn}')->references('${rel.toColumn}')->on('${rel.toTable}');\n`;
    });

    output += `    $table->timestamps();\n`;
    output += '});\n\n';
  });

  return output;
};
